/**
 * Biometric authentication hook using the Web Authentication API (WebAuthn).
 *
 * - register()     -- creates a platform authenticator credential (fingerprint / face)
 * - authenticate() -- verifies the biometric to unlock the vault
 * - Falls back to password entry when biometrics are unavailable
 *
 * Security notes:
 * - The relying party ID is derived from the current origin
 * - Challenge bytes are generated with crypto.getRandomValues (not from a server,
 *   since the vault is client-only; this prevents replay within the same session)
 * - Credential IDs are stored in localStorage (same-origin isolation)
 */

import { useState, useEffect, useCallback } from 'react';

const CREDENTIAL_STORAGE_KEY = 'kaimed-vault-webauthn-credential';

interface BiometricAuthState {
  /** Whether the platform supports WebAuthn with a platform authenticator */
  isSupported: boolean;
  /** Whether a credential has already been registered */
  isRegistered: boolean;
  /** Register a new platform authenticator credential */
  register: () => Promise<boolean>;
  /** Authenticate with the registered credential */
  authenticate: () => Promise<boolean>;
  /** Last error message, if any */
  error: string | null;
  /** Clear the current error */
  clearError: () => void;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToArrayBuffer(b64url: string): ArrayBuffer {
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4 !== 0) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function useBiometricAuth(): BiometricAuthState {
  const [isSupported, setIsSupported] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detect platform authenticator support
  useEffect(() => {
    let cancelled = false;

    async function detect() {
      try {
        if (
          typeof window === 'undefined' ||
          !window.PublicKeyCredential ||
          !PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable
        ) {
          return;
        }
        const available =
          await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (!cancelled) {
          setIsSupported(available);
        }
      } catch {
        // Silently degrade -- biometrics just won't be offered
      }
    }

    detect();

    // Check for stored credential
    const stored = localStorage.getItem(CREDENTIAL_STORAGE_KEY);
    if (stored) {
      setIsRegistered(true);
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Register a new platform authenticator credential.
   */
  const register = useCallback(async (): Promise<boolean> => {
    setError(null);

    if (!isSupported) {
      setError('Biometric authentication is not supported on this device.');
      return false;
    }

    try {
      const challenge = crypto.getRandomValues(new Uint8Array(32));
      const userId = crypto.getRandomValues(new Uint8Array(16));

      const createOptions: PublicKeyCredentialCreationOptions = {
        challenge,
        rp: {
          name: 'KaiMed Patient Vault',
          id: window.location.hostname,
        },
        user: {
          id: userId,
          name: 'patient@kaimed.local',
          displayName: 'KaiMed Patient',
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256
          { alg: -257, type: 'public-key' },  // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform',
          userVerification: 'required',
          residentKey: 'preferred',
        },
        timeout: 60000,
        attestation: 'none', // We don't need attestation for local vault
      };

      const credential = (await navigator.credentials.create({
        publicKey: createOptions,
      })) as PublicKeyCredential | null;

      if (!credential) {
        setError('Credential creation was cancelled.');
        return false;
      }

      // Store the credential ID for later authentication
      const credentialId = arrayBufferToBase64Url(credential.rawId);
      localStorage.setItem(CREDENTIAL_STORAGE_KEY, credentialId);
      setIsRegistered(true);
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to register biometric.';
      setError(message);
      return false;
    }
  }, [isSupported]);

  /**
   * Authenticate using the previously registered credential.
   */
  const authenticate = useCallback(async (): Promise<boolean> => {
    setError(null);

    const storedCredentialId = localStorage.getItem(CREDENTIAL_STORAGE_KEY);
    if (!storedCredentialId) {
      setError('No biometric credential registered. Please set up biometrics first.');
      return false;
    }

    try {
      const challenge = crypto.getRandomValues(new Uint8Array(32));

      const getOptions: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [
          {
            id: base64UrlToArrayBuffer(storedCredentialId),
            type: 'public-key',
            transports: ['internal'],
          },
        ],
        userVerification: 'required',
        timeout: 60000,
      };

      const assertion = (await navigator.credentials.get({
        publicKey: getOptions,
      })) as PublicKeyCredential | null;

      if (!assertion) {
        setError('Biometric authentication was cancelled.');
        return false;
      }

      // In a client-only vault the successful completion of the ceremony
      // (user verification = required) is sufficient proof of identity.
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Biometric authentication failed.';
      setError(message);
      return false;
    }
  }, []);

  return {
    isSupported,
    isRegistered,
    register,
    authenticate,
    error,
    clearError,
  };
}
